import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Matrix, Quaternion, Vector2, Vector3 } from '@babylonjs/core/Maths';
import { CreateBox, CreateCylinder, CreatePlane, CreateSphere } from '@babylonjs/core/Meshes/Builders';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Scene } from '@babylonjs/core/scene';
import { eUvMode } from '@models/classes/enums';
import { UvMapFeature } from '@models/classes/features/uv-map-feature';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { SubmeshVertexData } from './geometry-helper';
import { applyMatrix4, computeNormal, rotationToVector } from './math-helper';

const pWorking = new Vector3();
const p1 = new Vector3();
const p2 = new Vector3();
const p3 = new Vector3();
const uv1 = new Vector2();
const uv2 = new Vector2();
const uv3 = new Vector2();
const workingUv = new Vector2();
const workingUv2 = new Vector2();
const working1 = new Vector3();

export class UVMapHelper {
    generateUVMapInPlace(
        vertexData: Partial<SubmeshVertexData>,
        feature: UvMapFeature,
        indexList?: number[]
    ): Partial<SubmeshVertexData> {
        const newControlPointMap: Record<string, number> = {};
        const axis = feature.projectionAxis.toVec3();
        const rotation = rotationToVector(axis, new Vector3(0, 1, 0));
        rotation.multiplyInPlace(Quaternion.RotationAxis(new Vector3(0, 1, 0), (feature.angle / 180) * Math.PI));
        // const matrixRotation = new Matrix();
        // Matrix.FromQuaternionToRef(rotation, matrixRotation);
        const transformMatrix = Matrix.Compose(
            feature.scale ? feature.scale.toVec3() : Vector3.One(),
            rotation,
            Vector3.Zero()
        );

        const transformMatrixInv = Matrix.Invert(transformMatrix);

        if (!vertexData.positions) {
            throw Error('No vertex positions on mesh');
        }
        if (!vertexData.indices) {
            throw Error('No vertex indices on mesh');
        }
        const geometryPositions = vertexData.positions;
        const geometryIndices = vertexData.indices;

        const controlPointCount = geometryPositions.length / 3;

        const newUvs =
            vertexData.uvs && vertexData.uvs.length === controlPointCount * 2
                ? new Float32Array(vertexData.uvs)
                : new Float32Array(controlPointCount * 2);
        const appendVertexData = {
            positions: [] as number[],
            normals: [] as number[],
            uvs: [] as number[],
        };

        const nbFaces = indexList ? indexList.length / 3 : geometryIndices.length / 3;
        const touchedControlPoints = new Int8Array(controlPointCount);

        if (indexList) {
            indexList.forEach(index => {
                touchedControlPoints[geometryIndices[index]] = 1;
            });
        }
        const outputUvs = [uv1, uv2, uv3];

        for (let i = 0; i < nbFaces; i++) {
            /** here we generate the new UVs */
            let i1: number;
            let i2: number;
            let i3: number;
            if (indexList) {
                i1 = indexList[i * 3];
                i2 = indexList[i * 3 + 1];
                i3 = indexList[i * 3 + 2];
            } else {
                i1 = i * 3;
                i2 = i * 3 + 1;
                i3 = i * 3 + 2;
            }
            const v1 = geometryIndices[i1];
            const v2 = geometryIndices[i2];
            const v3 = geometryIndices[i3];

            if (feature.mode === eUvMode.useExisting) {
                if (vertexData.uvs) {
                    const angle = (feature.angle / 180) * Math.PI;
                    const vertices = [v1, v2, v3];

                    for (let i = 0; i < 3; i++) {
                        const uvIdx = vertices[i] * 2;

                        const u = vertexData.uvs[uvIdx] - 0.5;
                        const v = vertexData.uvs[uvIdx + 1] - 0.5;

                        let newU = u * Math.cos(angle) - v * Math.sin(angle) + 0.5;
                        let newV = u * Math.sin(angle) + v * Math.cos(angle) + 0.5;
                        newU = newU * feature.uTile + feature.uOffset;
                        newV = newV * feature.vTile + feature.vOffset;

                        outputUvs[i].copyFromFloats(newU, newV);
                    }
                }
            } else {
                const start = v1 * 3;
                p1.copyFromFloats(geometryPositions[start], geometryPositions[start + 1], geometryPositions[start + 2]);

                const start2 = v2 * 3;
                p2.copyFromFloats(
                    geometryPositions[start2],
                    geometryPositions[start2 + 1],
                    geometryPositions[start2 + 2]
                );

                const start3 = v3 * 3;
                p3.copyFromFloats(
                    geometryPositions[start3],
                    geometryPositions[start3 + 1],
                    geometryPositions[start3 + 2]
                );
                const normal = computeNormal([p1, p2, p3]);

                applyMatrix4(normal, transformMatrixInv);

                if (feature.center) {
                    const center = feature.center.toVec3();
                    p1.subtractInPlace(center);
                    p2.subtractInPlace(center);
                    p3.subtractInPlace(center);
                }
                this.updateFaceUVMapToRef(normal, p1, feature, transformMatrixInv, uv1);
                this.updateFaceUVMapToRef(normal, p2, feature, transformMatrixInv, uv2);
                this.updateFaceUVMapToRef(normal, p3, feature, transformMatrixInv, uv3);

                /**
                 * Find the UV at the midpoint position between each edge of the polygon.
                 * This should closely match the midpoints of the generated vertex UVs.If it does not, we must wrap the UVs at that edge to avoid a UV seam.
                 * The tolerance is an arbitrary number to avoid artifacts. If wrapping is occuring when it shouldn't, increase this number.
                 * If wrapping is not occurring when it should, reduce it.
                 * As you try to wrap a uv on polygons that are not arranged in a good way to accept them (i.e wrap a sphere onto a 8-vertex cube), the error will become significant.
                 */
                const tolerance = 0.05;
                working1.copyFrom(p1);
                this.updateFaceUVMapToRef(
                    normal,
                    working1.addInPlace(p2).scaleInPlace(0.5),
                    feature,
                    transformMatrixInv,
                    workingUv
                );
                workingUv2.copyFrom(uv1);
                workingUv2.addInPlace(uv2).scaleInPlace(0.5);
                workingUv.subtractInPlace(workingUv2);
                if (Math.abs(workingUv.x % 1) > tolerance) {
                    if (uv1.x > uv2.x) uv1.x -= 1;
                    else uv2.x -= 1;
                }
                if (Math.abs(workingUv.y % 1) > tolerance) {
                    if (uv1.y > uv2.y) uv1.y -= 1;
                    else uv2.y -= 1;
                }

                if (uv2.x >= 0 && uv2.y >= 0) {
                    working1.copyFrom(p2);
                    this.updateFaceUVMapToRef(
                        normal,
                        working1.addInPlace(p3).scaleInPlace(0.5),
                        feature,
                        transformMatrixInv,
                        workingUv
                    );
                    workingUv2.copyFrom(uv2);
                    workingUv2.addInPlace(uv3).scaleInPlace(0.5);
                    workingUv.subtractInPlace(workingUv2);
                    if (Math.abs(workingUv.x % 1) > tolerance) {
                        if (uv2.x > uv3.x) uv2.x -= 1;
                        else uv3.x -= 1;
                    }
                    if (Math.abs(workingUv.y % 1) > tolerance) {
                        if (uv2.y > uv3.y) uv2.y -= 1;
                        else uv3.y -= 1;
                    }
                }

                if (uv1.x >= 0 && uv1.y >= 0 && uv3.x >= 0 && uv3.y >= 0) {
                    working1.copyFrom(p1);
                    this.updateFaceUVMapToRef(
                        normal,
                        working1.addInPlace(p3).scaleInPlace(0.5),
                        feature,
                        transformMatrixInv,
                        workingUv
                    );
                    workingUv2.copyFrom(uv1);
                    workingUv2.addInPlace(uv3).scaleInPlace(0.5);
                    workingUv.subtractInPlace(workingUv2);
                    if (Math.abs(workingUv.x % 1) > tolerance) {
                        if (uv1.x > uv3.x) uv1.x -= 1;
                        else uv3.x -= 1;
                    }
                    if (Math.abs(workingUv.y % 1) > tolerance) {
                        if (uv1.y > uv3.y) uv1.y -= 1;
                        else uv3.y -= 1;
                    }
                }
            }
            /** Done generating the new UVs, stored in uv1, uv2 and uv3 for this polygon */

            if (touchedControlPoints[v1]) {
                expandControlPoints(i1, v1, uv1);
            } else {
                newUvs[v1 * 2] = uv1.x;
                newUvs[v1 * 2 + 1] = uv1.y;
            }

            if (touchedControlPoints[v2]) {
                expandControlPoints(i2, v2, uv2);
            } else {
                newUvs[v2 * 2] = uv2.x;
                newUvs[v2 * 2 + 1] = uv2.y;
            }

            if (touchedControlPoints[v3]) {
                expandControlPoints(i3, v3, uv3);
            } else {
                newUvs[v3 * 2] = uv3.x;
                newUvs[v3 * 2 + 1] = uv3.y;
            }

            touchedControlPoints[v1] = 1;
            touchedControlPoints[v2] = 1;
            touchedControlPoints[v3] = 1;
        }

        if (appendVertexData.positions.length > 0) {
            const newPositions = new Float32Array(vertexData.positions.length + appendVertexData.positions.length);
            newPositions.set(vertexData.positions);
            newPositions.set(appendVertexData.positions, vertexData.positions.length);
            vertexData.positions = newPositions;
        }
        if (vertexData.normals && appendVertexData.normals.length > 0) {
            const newNormals = new Float32Array(vertexData.normals.length + appendVertexData.normals.length);
            newNormals.set(vertexData.normals);
            newNormals.set(appendVertexData.normals, vertexData.normals.length);
            vertexData.normals = newNormals;
        }
        if (appendVertexData.uvs.length > 0) {
            const newUvsTyped = new Float32Array(newUvs.length + appendVertexData.uvs.length);
            newUvsTyped.set(newUvs);
            newUvsTyped.set(appendVertexData.uvs, newUvs.length);
            vertexData.uvs = newUvsTyped;
        } else {
            vertexData.uvs = new Float32Array(newUvs);
        }

        return vertexData;

        function expandControlPoints(index: number, vertexIndex: number, newUv: Vector2) {
            if (newUvs[vertexIndex * 2] !== newUv.x || newUvs[vertexIndex * 2 + 1] !== newUv.y) {
                if (!vertexData.positions || !vertexData.indices) {
                    throw Error('Invalid geometry: positions or indices are null');
                }
                const indices = vertexData.indices;
                const positions = vertexData.positions;
                const normals = vertexData.normals;

                const key =
                    positions[vertexIndex * 3].toPrecision(6) +
                    ',' +
                    positions[vertexIndex * 3 + 1].toPrecision(6) +
                    ',' +
                    positions[vertexIndex * 3 + 2].toPrecision(6) +
                    '_' +
                    (normals
                        ? normals[vertexIndex * 3].toPrecision(6) +
                          ',' +
                          normals[vertexIndex * 3 + 1].toPrecision(6) +
                          ',' +
                          normals[vertexIndex * 3 + 2].toPrecision(6) +
                          '_'
                        : '') +
                    newUv.x.toPrecision(6) +
                    ',' +
                    newUv.y.toPrecision(6);

                if (newControlPointMap[key]) {
                    indices[index] = newControlPointMap[key];
                } else {
                    const newVertexIndex = (positions.length + appendVertexData.positions.length) / 3;
                    indices[index] = newVertexIndex;

                    appendVertexData.positions.push(...positions.slice(vertexIndex * 3, vertexIndex * 3 + 3));
                    if (vertexData.normals) {
                        appendVertexData.normals.push(
                            ...vertexData.normals.slice(vertexIndex * 3, vertexIndex * 3 + 3)
                        );
                    }
                    appendVertexData.uvs.push(newUv.x, newUv.y);

                    if (vertexData.submeshData) {
                        for (let i = 0; i < vertexData.submeshData.length; i++) {
                            const submeshData = vertexData.submeshData[i];
                            if (
                                submeshData.verticesStart <= vertexIndex &&
                                submeshData.verticesStart + submeshData.verticesCount > vertexIndex
                            ) {
                                submeshData.verticesCount += 1;
                            } else if (submeshData.verticesStart > vertexIndex) {
                                submeshData.verticesStart += 1;
                            }
                        }
                    }

                    newControlPointMap[key] = newVertexIndex;
                }
            }
        }
    }

    computeAutoFitSize(mesh: AbstractMesh): KbVector {
        const boundingBox = mesh.getBoundingInfo().boundingBox;

        //const center = boundingBox.center;

        const max = boundingBox.maximum;
        const min = boundingBox.minimum;

        return new KbVector(max.x - min.x, max.y - min.y, max.z - min.z);
    }

    private updateFaceUVMapToRef(
        faceNormal: Vector3,
        position: Vector3,
        feat: UvMapFeature,
        transformMatrixInv: Matrix,
        uv: Vector2
    ): Vector2 {
        pWorking.copyFrom(position);
        applyMatrix4(pWorking, transformMatrixInv);

        switch (feat.mode) {
            case eUvMode.planar:
                this.planarUVMap(pWorking, uv);
                break;
            case eUvMode.box:
                this.boxUVMap(pWorking, faceNormal, uv);
                break;
            case eUvMode.cylindrical:
                this.cylindricalUVMap(pWorking, uv);
                break;
            case eUvMode.spherical:
                this.sphericalUVMap(pWorking, uv);
                break;
            case eUvMode.useExisting:
                //don't do anything so that existing UVs of mesh are being used instead
                break;
            default:
                uv.set(0, 0);
                break;
        }
        if (feat.uTile && feat.uTile !== 1) {
            uv.x *= feat.uTile;
        }
        if (feat.vTile && feat.vTile !== 1) {
            uv.y *= feat.vTile;
        }
        return uv;
    }

    private planarUVMap(vertex: Vector3, uv: Vector2): Vector2 {
        let u = vertex.x;
        let v = -vertex.z;

        u = u + 0.5;
        v = v + 0.5;
        uv.set(u, v);
        return uv;
    }

    private cylindricalUVMap(vertex: Vector3, uv: Vector2): Vector2 {
        let lon;
        if (vertex.x === 0 && vertex.z === 0) {
            lon = 0;
        } else {
            lon = Math.atan2(vertex.x, vertex.z);
            if (lon < 0) {
                lon += Math.PI * 2;
            }
        }
        const t = vertex.y + 0.5;
        lon = lon / (Math.PI * 2);
        uv.set(lon, t);
        return uv;
    }

    private boxUVMap(vertex: Vector3, normal: Vector3, uv: Vector2): Vector2 {
        let s = 0,
            t = 0;
        const x = vertex.x,
            y = vertex.y,
            z = vertex.z;
        const nx = Math.abs(normal.x),
            ny = Math.abs(normal.y),
            nz = Math.abs(normal.z);
        if (nx >= ny && nx >= nz) {
            s = -z + 0.5;
            t = y + 0.5;
        }
        if (ny >= nx && ny >= nz) {
            s = x + 0.5;
            t = -z + 0.5;
        }
        if (nz >= nx && nz >= ny) {
            s = x + 0.5;
            t = y + 0.5;
        }
        // not sure what this is for but it screws up the mapping and flips the uv's halfway through
        // if (normal.x < 0) {
        //     s = -s + 1
        // }
        // if (normal.y < 0) {
        //     s = -s + 1
        // }
        // if (normal.z < 0) {
        //     s = -s + 1
        // }
        uv.set(s, t);
        return uv;
    }

    private sphericalUVMap(vertex: Vector3, uv: Vector2): Vector2 {
        const newVertex = vertex.clone();
        const lonlat = xyzToLongitudeLatitude(newVertex.x, -newVertex.y, newVertex.z);
        const lon = lonlat.lon / (Math.PI * 2);
        const lat = 0.5 - lonlat.lat / Math.PI;
        uv.set(lon, lat);
        return uv;

        function xyzToLongitudeLatitude(x: number, y: number, z: number) {
            let lo: number, la: number;
            const EPSILON = 1e-6;
            if (Math.abs(x) < EPSILON && Math.abs(z) < EPSILON) {
                lo = 0;
                if (Math.abs(y) < EPSILON) {
                    la = 0;
                } else {
                    la = y < 0 ? -(Math.PI / 2) : Math.PI / 2;
                }
            } else {
                lo = Math.atan2(x, z);
                if (lo < 0) {
                    lo += Math.PI * 2;
                }
                const h = Math.sqrt(x * x + z * z);
                la = Math.atan2(y, h);
            }
            return {
                lon: lo,
                lat: la,
            };
        }
    }
}

export function createUvMapVisualization(uvMap: UvMapFeature, scene: Scene, color?: Color3) {
    let uvMapVisualization: Mesh;

    if (!color) {
        color = Color3.Yellow();
    }
    const coloredMaterial = new StandardMaterial('', scene);
    coloredMaterial.wireframe = true;
    coloredMaterial.emissiveColor = color;

    if (uvMap.mode === eUvMode.spherical) {
        uvMapVisualization = CreateSphere('UvMapSphere', { segments: 6 }, scene);
    } else if (uvMap.mode === eUvMode.cylindrical) {
        uvMapVisualization = CreateCylinder('UvMapCylinder', { tessellation: 10 }, scene);
    } else if (uvMap.mode === eUvMode.box) {
        uvMapVisualization = CreateBox('UvBox', {}, scene);
    } else if (uvMap.mode === eUvMode.planar) {
        uvMapVisualization = CreatePlane('mirrorPlane', {}, scene);
        uvMapVisualization.rotation = new Vector3(Math.PI / 2, 0, 0);
        uvMapVisualization.bakeCurrentTransformIntoVertices();
    } else {
        uvMapVisualization = CreatePlane('mirrorPlane', {}, scene);
        uvMapVisualization.bakeCurrentTransformIntoVertices();
        uvMapVisualization.setEnabled(false);
    }
    uvMapVisualization.material = coloredMaterial;

    return uvMapVisualization;
}
